import { useEffect, useMemo } from 'react';
import { useLazyQuery } from '@apollo/client';

import { getUserLocationCookie } from 'commons/localStorage';
import usePdpSelectedCustomizerTab from 'components/consumer/PDP/state/pdpSelectedCustomizerTab/usePdpSelectedCustomizerTab';
import usePDPOptions from 'components/consumer/PDP/state/context/usePDPOptions';
import useZipCode from 'global-state/zipCode/useZipCode';
import usePDPBasicDetails from 'components/consumer/PDP/state/context/usePDPBasicDetails';
import { PDP_READYTOSHIP_TAB_ID } from '../../commons/constants';
import { GET_PRODUCT_OPTIONS_LEAD_TIMES } from './queries';

const getLeadTimeErrorText = leadTimeError =>
  leadTimeError?.code === '90001'
    ? 'Option currently not available'
    : leadTimeError?.message;

const getLeadTimeText = leadTime =>
  leadTime > 7 ? ` ${Math.round(leadTime / 7)} weeks` : ` ${leadTime} days`;

const usePDPLeadTime = () => {
  const zipCode = useZipCode();
  const { data: productBasicData } = usePDPBasicDetails();
  const pdpSelectedCustomizerTab = usePdpSelectedCustomizerTab();

  const {
    loading: productOptionsLoading,
    data: { selectedOptions },
  } = usePDPOptions();

  const [
    getProductLeadTimes,
    { loading: leadTimeLoading, data: leadTimesResponse },
  ] = useLazyQuery(GET_PRODUCT_OPTIONS_LEAD_TIMES);

  const productId = productBasicData?.id;

  const shippingLeadTime = useMemo(() => {
    if (leadTimesResponse?.getProductOptionsLeadTime) {
      const { getProductOptionsLeadTime: leadTimesData } = leadTimesResponse;
      if (leadTimesData.errors?.length) {
        return getLeadTimeErrorText(leadTimesData?.error[0]);
      }
      return getLeadTimeText(leadTimesData?.lead_time);
    }
    return '';
  }, [leadTimesResponse]);

  useEffect(() => {
    if (!zipCode || productOptionsLoading || leadTimeLoading || !productId) {
      return;
    }
    const stockForOptions = {};
    Object.keys(selectedOptions).forEach(optId => {
      stockForOptions[optId] = [selectedOptions[optId]];
    });
    const userLocation = getUserLocationCookie();

    getProductLeadTimes({
      variables: {
        id: productId,
        zipcode: userLocation.zip ? userLocation.zip.toString() : '',
        options: JSON.stringify(stockForOptions),
        noCache: pdpSelectedCustomizerTab === PDP_READYTOSHIP_TAB_ID,
      },
    });
  }, [
    getProductLeadTimes,
    leadTimeLoading,
    pdpSelectedCustomizerTab,
    productId,
    productOptionsLoading,
    selectedOptions,
    zipCode,
  ]);

  return {
    shippingLeadTime,
    leadTimeLoading,
  };
};

export default usePDPLeadTime;
